import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowRight, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useToast } from "../../hooks/use-toast";
import { COMPANY } from "../../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = [
  "Under £2,500",
  "£2,500 – £5,000",
  "£5,000 – £10,000",
  "£10,000 – £25,000",
  "£25,000+",
];

const SERVICES_OPTS = [
  "Website design & build",
  "E-commerce",
  "Custom software / SaaS",
  "AI integration",
  "Social media & marketing",
  "Something else",
];

const WEB_SERVICES = ["Website design & build", "E-commerce"];

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "",
    budget: "",
    message: "",
    consent: false,
    webTerms: false,
  });
  const isWeb = WEB_SERVICES.includes(form.service);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (key, val) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  useEffect(() => {
    const onSelect = (e) => {
      setSubmitted(false);
      setForm((prev) => ({
        ...prev,
        service: e.detail.service,
        message: e.detail.message,
      }));
    };
    window.addEventListener("select-package", onSelect);
    return () => window.removeEventListener("select-package", onSelect);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !form.consent) {
      toast({
        title: "Missing information",
        description: "Please fill in your name, email, message and consent.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: form.company || null,
        phone: form.phone.trim() || null,
        service: form.service || null,
        budget: form.budget || null,
        message: form.message,
        consent: form.consent,
        web_terms_agreed: isWeb ? form.webTerms : null,
      });
      setSubmitted(true);
      toast({
        title: "Message sent!",
        description: "Thanks for reaching out. We'll be in touch shortly.",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail)
          ? err.response.data.detail[0]?.msg
          : null) ||
        "Something went wrong. Please try again or email us directly.";
      toast({
        title: "Could not send message",
        description:
          typeof detail === "string" ? detail : "Please check your details and try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <span className="section-eyebrow">Contact</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
              Let&rsquo;s build something great together.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Tell us a bit about your project and we&rsquo;ll get back within
              one working day with next steps — or a quick &ldquo;here&rsquo;s
              what we&rsquo;d do.&rdquo;
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`mailto:${COMPANY.email}`}
                className="flex items-start gap-4 group"
              >
                <div className="h-11 w-11 rounded-xl bg-slate-100 group-hover:bg-slate-900 group-hover:text-white transition-colors flex items-center justify-center shrink-0">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">
                    Email
                  </div>
                  <div className="text-slate-900 font-medium">
                    {COMPANY.email}
                  </div>
                </div>
              </a>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                className="flex items-start gap-4 group"
              >
                <div className="h-11 w-11 rounded-xl bg-slate-100 group-hover:bg-slate-900 group-hover:text-white transition-colors flex items-center justify-center shrink-0">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">
                    Phone
                  </div>
                  <div className="text-slate-900 font-medium">
                    {COMPANY.phone}
                  </div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">
                    Studio
                  </div>
                  <div className="text-slate-900 font-medium">
                    {COMPANY.address}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 lg:p-10">
              {submitted ? (
                <div className="text-center py-16">
                  <div className="mx-auto h-14 w-14 rounded-full bg-sky-100 flex items-center justify-center">
                    <Check className="h-7 w-7 text-sky-600" />
                  </div>
                  <h3 className="mt-5 text-2xl font-semibold text-slate-900">
                    Thanks, {form.name.split(" ")[0]}!
                  </h3>
                  <p className="mt-2 text-slate-600 max-w-md mx-auto">
                    Your message is on its way. We&rsquo;ll get back within one
                    working day at <strong>{form.email}</strong>.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-slate-700">
                        Your name *
                      </Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        placeholder="Jane Smith"
                        className="mt-2 bg-white border-slate-200 h-11"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-slate-700">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="jane@company.com"
                        className="mt-2 bg-white border-slate-200 h-11"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="company" className="text-slate-700">
                        Company
                      </Label>
                      <Input
                        id="company"
                        value={form.company}
                        onChange={(e) => handleChange("company", e.target.value)}
                        placeholder="Your business name"
                        className="mt-2 bg-white border-slate-200 h-11"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-slate-700">
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="contact-phone-input"
                        value={form.phone}
                        onChange={(e) => handleChange("phone", e.target.value.replace(/[^0-9+()\-\s]/g, ""))}
                        placeholder="07123 456789"
                        maxLength={30}
                        autoComplete="tel"
                        className="mt-2 bg-white border-slate-200 h-11"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-slate-700">I&rsquo;m interested in</Label>
                      <Select
                        value={form.service}
                        onValueChange={(v) => handleChange("service", v)}
                      >
                        <SelectTrigger data-testid="contact-service-select" className="mt-2 bg-white border-slate-200 h-11">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICES_OPTS.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-700">Approx. budget</Label>
                      <Select
                        value={form.budget}
                        onValueChange={(v) => handleChange("budget", v)}
                      >
                        <SelectTrigger className="mt-2 bg-white border-slate-200 h-11">
                          <SelectValue placeholder="Pick a range" />
                        </SelectTrigger>
                        <SelectContent>
                          {BUDGETS.map((b) => (
                            <SelectItem key={b} value={b}>
                              {b}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-slate-700">
                      Tell us about your project *
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message-input"
                      value={form.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      placeholder="A few lines about goals, timing and anything else useful..."
                      className="mt-2 bg-white border-slate-200 min-h-[140px]"
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consent"
                      checked={form.consent}
                      onCheckedChange={(v) => handleChange("consent", !!v)}
                      className="mt-1"
                    />
                    <Label
                      htmlFor="consent"
                      className="text-xs text-slate-600 leading-relaxed font-normal"
                    >
                      I agree my information will be stored and processed for
                      the purpose of getting back to me. I can revoke consent
                      at any time. *
                    </Label>
                  </div>

                  {isWeb && (
                    <div className="flex items-start gap-3" data-testid="contact-web-terms-row">
                      <Checkbox
                        id="webTerms"
                        data-testid="contact-web-terms-checkbox"
                        checked={form.webTerms}
                        onCheckedChange={(v) => handleChange("webTerms", !!v)}
                        className="mt-1"
                      />
                      <Label htmlFor="webTerms" className="text-xs text-slate-600 leading-relaxed font-normal">
                        I agree to the{" "}
                        <Link to="/web-design-terms" target="_blank" className="underline text-slate-900">
                          Web Design Terms
                        </Link>{" "}
                        (50% deposit, balance on completion; the website remains the property of{" "}
                        {COMPANY.name} until paid in full).
                      </Label>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full sm:w-auto h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white px-7 group"
                  >
                    {loading ? "Sending..." : "Send message"}
                    <ArrowRight className="ml-1.5 h-4 w-4 btn-arrow" />
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
